import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Loader2, Minus, Plus, ShoppingBag, Tag, Trash2, X } from "lucide-react";
import { Lock } from "@phosphor-icons/react";
import { useServerFn } from "@tanstack/react-start";
import { createCheckout } from "@/lib/checkout.functions";
import { useCart } from "@/lib/ecom/cart";
import { useCurrency } from "@/lib/ecom/currency";
import { formatMoney, productBySlug } from "@/lib/ecom/catalog";
import { useLocale } from "@/lib/i18n/locale";
import imgMetal from "@/assets/products/custom-black-metal.webp";
import imgGold from "@/assets/products/custom-gold.webp";
import imgBamboo from "@/assets/products/custom-bamboo.webp";
import imgWhitePvc from "@/assets/products/custom-white-pvc.webp";
import imgBlackPvc from "@/assets/products/custom-black-pvc.webp";
import imgGoogle from "@/assets/products/retail-google-review.webp";

const T = {
  en: {
    openCart: (n: number) => `Open cart (${n} items)`,
    yourCart: "Your cart",
    empty: "Empty",
    item: "item",
    items: "items",
    closeCart: "Close cart",
    cartEmpty: "Your cart is empty",
    browseShop: "Browse the shop to find your perfect card.",
    goToShop: "Go to shop",
    remove: "Remove",
    decrease: "Decrease",
    increase: "Increase",
    applied: "Applied",
    removeBtn: "Remove",
    haveCoupon: "Have a coupon?",
    enterCode: "Enter code",
    apply: "Apply",
    invalidCoupon: "Invalid coupon",
    subtotal: "Subtotal",
    bundleDiscount: "Bundle discount",
    coupon: "Coupon",
    total: "Total",
    starting: "Starting…",
    secureCheckout: "Secure Checkout",
    joinPros: "Join the 50,000+ professionals already using Banana",
    couldNotStart: "Could not start checkout",
  },
  vi: {
    openCart: (n: number) => `Mở giỏ hàng (${n} sản phẩm)`,
    yourCart: "Giỏ hàng của bạn",
    empty: "Trống",
    item: "sản phẩm",
    items: "sản phẩm",
    closeCart: "Đóng giỏ hàng",
    cartEmpty: "Giỏ hàng của bạn đang trống",
    browseShop: "Khám phá cửa hàng để tìm chiếc thẻ hoàn hảo.",
    goToShop: "Đến cửa hàng",
    remove: "Xóa",
    decrease: "Giảm",
    increase: "Tăng",
    applied: "Đã áp dụng",
    removeBtn: "Gỡ",
    haveCoupon: "Có mã giảm giá?",
    enterCode: "Nhập mã",
    apply: "Áp dụng",
    invalidCoupon: "Mã không hợp lệ",
    subtotal: "Tạm tính",
    bundleDiscount: "Giảm theo gói",
    coupon: "Mã giảm giá",
    total: "Tổng cộng",
    starting: "Đang bắt đầu…",
    secureCheckout: "Thanh toán an toàn",
    joinPros: "Tham gia cùng hơn 50.000 chuyên gia đang dùng Banana",
    couldNotStart: "Không thể bắt đầu thanh toán",
  },
  el: {
    openCart: (n: number) => `Άνοιγμα καλαθιού (${n} προϊόντα)`,
    yourCart: "Το καλάθι σας",
    empty: "Άδειο",
    item: "προϊόν",
    items: "προϊόντα",
    closeCart: "Κλείσιμο καλαθιού",
    cartEmpty: "Το καλάθι σας είναι άδειο",
    browseShop: "Δείτε το κατάστημα για να βρείτε την ιδανική κάρτα.",
    goToShop: "Προς το κατάστημα",
    remove: "Αφαίρεση",
    decrease: "Μείωση",
    increase: "Αύξηση",
    applied: "Εφαρμόστηκε",
    removeBtn: "Αφαίρεση",
    haveCoupon: "Έχετε κουπόνι;",
    enterCode: "Εισάγετε κωδικό",
    apply: "Εφαρμογή",
    invalidCoupon: "Μη έγκυρο κουπόνι",
    subtotal: "Υποσύνολο",
    bundleDiscount: "Έκπτωση πακέτου",
    coupon: "Κουπόνι",
    total: "Σύνολο",
    starting: "Έναρξη…",
    secureCheckout: "Ασφαλής πληρωμή",
    joinPros: "Γίνετε κι εσείς ένας από τους 50.000+ επαγγελματίες που χρησιμοποιούν τη Banana",
    couldNotStart: "Δεν ήταν δυνατή η έναρξη της πληρωμής",
  },
} as const;

const PRODUCT_IMAGES: Record<string, string> = {
  "metal": imgMetal,
  "24kt-gold": imgGold,
  "bamboo": imgBamboo,
  "white-pvc": imgWhitePvc,
  "black-pvc": imgBlackPvc,
  "google-review": imgGoogle,
};

export function CartButton({ className = "" }: { className?: string }) {
  const { count, setOpen } = useCart();
  const t = T[useLocale()];
  return (
    <button
      type="button"
      onClick={() => setOpen(true)}
      aria-label={t.openCart(count)}
      className={`relative inline-flex h-9 w-9 items-center justify-center rounded-full text-foreground transition hover:bg-muted ${className}`}
    >
      <ShoppingBag className="h-[18px] w-[18px]" strokeWidth={1.75} />
      {count > 0 && (
        <span className="absolute -right-0.5 -top-0.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-brand-lime px-1 text-[10px] font-bold leading-none text-brand-lime-foreground">
          {count > 99 ? "99+" : count}
        </span>
      )}
    </button>
  );
}

export function CartDrawer() {
  const {
    items, open, setOpen, removeItem, updateQty,
    subtotal, bundleDiscount, couponDiscount, total, coupon,
    applyCoupon, removeCoupon,
  } = useCart();
  const { country } = useCurrency();
  const t = T[useLocale()];
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const createCheckoutFn = useServerFn(createCheckout);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;

  const onApply = async () => {
    if (!code.trim()) return;
    setBusy(true); setErr(null);
    const r = await applyCoupon(code);
    setBusy(false);
    if (!r.ok) setErr(r.message || t.invalidCoupon);
    else setCode("");
  };

  const onCheckout = async () => {
    if (items.length === 0 || creating) return;
    setCreating(true);
    try {
      const res = await createCheckoutFn({
        data: {
          items: items.map((it) => ({ slug: it.slug, bundleQty: it.bundleQty, qty: it.qty })),
          countryCode: country.code,
          couponCode: coupon?.code ?? null,
          // Affiliate attribution is opt-in: the user must enter the partner's
          // code in the coupon field. We pass the same code as a candidate
          // affiliate code; the server resolves it only if it matches.
          affiliateCode: coupon?.code ?? null,
        },
      });
      // Hand off to the shared checkout service at checkout.banana.cards.
      window.location.href = res.redirectUrl;
    } catch (e) {
      setErr(e instanceof Error ? e.message : t.couldNotStart);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] animate-fade-in">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setOpen(false)} />
      <aside className="absolute right-0 top-0 flex h-full w-full max-w-md animate-slide-in-right flex-col bg-background">
        <header className="relative overflow-hidden border-b border-foreground/10 px-5 py-5">
          <div
            className="pointer-events-none absolute -right-20 -top-24 -z-0 h-56 w-56 rounded-full opacity-50 blur-3xl"
            style={{ background: "radial-gradient(circle, oklch(0.96 0.21 117 / 0.45), transparent 70%)" }}
            aria-hidden
          />
          <div className="relative flex items-center justify-between">
            <div>
              <p className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-muted-foreground">{t.yourCart}</p>
              <p className="mt-0.5 font-display text-2xl leading-none">
                {items.length === 0 ? t.empty : `${items.length} ${items.length === 1 ? t.item : t.items}`}
              </p>
            </div>
            <button onClick={() => setOpen(false)} className="rounded-full bg-card p-2 transition hover:bg-muted" aria-label={t.closeCart}>
              <X className="h-4 w-4" />
            </button>
          </div>
        </header>

        <div className="flex-1 overflow-auto px-5 py-4">
          {items.length === 0 ? (
            <div className="mt-16 text-center">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-muted">
                <ShoppingBag className="h-6 w-6 text-foreground/60" />
              </div>
              <p className="mt-4 text-sm font-semibold">{t.cartEmpty}</p>
              <p className="mt-1 text-xs text-muted-foreground">{t.browseShop}</p>
              <Link to="/shop" onClick={() => setOpen(false)} className="mt-4 inline-flex items-center rounded-xl bg-foreground px-4 py-2 text-xs font-bold text-background">
                {t.goToShop}
              </Link>
            </div>
          ) : (
            <ul className="space-y-3">
              {items.map((it, idx) => {
                const p = productBySlug(it.slug);
                if (!p) return null;
                const b = p.bundles.find((x) => x.qty === it.bundleQty) ?? p.bundles[0];
                const base = p.prices[country.code] ?? p.prices.US ?? 0;
                const unit = base * b.perCard;
                const lineQty = b.qty * it.qty;
                const lineTotal = unit * lineQty;
                const lineCompareAt = base * lineQty;
                const img = PRODUCT_IMAGES[it.slug];
                return (
                  <li
                    key={idx}
                    className="group relative overflow-hidden rounded-2xl bg-card p-3 transition  "
                  >
                    <div className="flex gap-3">
                      <div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-xl bg-muted ring-1 ring-foreground/5">
                        {img ? (
                          <img src={img} alt={p.name} className="h-full w-full object-cover" loading="lazy" />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center text-muted-foreground">
                            <ShoppingBag className="h-5 w-5" />
                          </div>
                        )}
                        {b.save && (
                          <span className="absolute left-1 top-1 inline-flex items-center rounded-full bg-brand-lime px-1.5 py-0.5 text-[9px] font-bold leading-none text-brand-lime-foreground">
                            −{b.save}%
                          </span>
                        )}
                      </div>
                      <div className="flex min-w-0 flex-1 flex-col">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold leading-tight">{p.name}</p>
                            <p className="mt-1 text-[11px] text-muted-foreground">{b.label}</p>
                          </div>
                          <button
                            onClick={() => removeItem(idx)}
                            className="-mr-1 -mt-1 rounded-full p-1.5 text-muted-foreground transition hover:bg-muted hover:text-foreground"
                            aria-label={t.remove}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        <div className="mt-auto flex items-end justify-between pt-2">
                          <div className="flex items-center gap-0.5 rounded-full border border-foreground/15 bg-background px-1 py-0.5">
                            <button onClick={() => updateQty(idx, it.qty - 1)} className="rounded-full p-1 hover:bg-muted" aria-label={t.decrease}>
                              <Minus className="h-3 w-3" />
                            </button>
                            <span className="min-w-[2ch] text-center text-[11px] font-bold tabular-nums">{it.qty}</span>
                            <button onClick={() => updateQty(idx, it.qty + 1)} className="rounded-full p-1 hover:bg-muted" aria-label={t.increase}>
                              <Plus className="h-3 w-3" />
                            </button>
                          </div>
                          <div className="text-right leading-tight">
                            <p className="text-sm font-bold tabular-nums">{formatMoney(lineTotal, country)}</p>
                            {b.save && (
                              <p className="text-[10.5px] text-muted-foreground line-through tabular-nums">{formatMoney(lineCompareAt, country)}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <footer className="border-t border-foreground/10 bg-card/40 px-5 py-4">
            {/* Coupon box */}
            <div className="mb-4 rounded-2xl border border-dashed border-foreground/20 bg-background/60 p-3">
              {coupon ? (
                <div className="flex items-center justify-between gap-2">
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand-lime/20 text-foreground">
                      <Tag className="h-3.5 w-3.5" />
                    </span>
                    <div className="min-w-0">
                      <p className="truncate text-xs font-bold uppercase tracking-wider">{coupon.code}</p>
                      <p className="text-[11px] text-muted-foreground">
                        {t.applied} · {coupon.type === "percent" ? `−${coupon.value}%` : `−${formatMoney(coupon.value, country)}`}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={removeCoupon}
                    className="shrink-0 rounded-full border border-foreground/15 bg-card px-3 py-1 text-[11px] font-semibold transition hover:bg-muted"
                  >
                    {t.removeBtn}
                  </button>
                </div>
              ) : (
                <>
                  <div className="mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
                    <Tag className="h-3 w-3" />
                    {t.haveCoupon}
                  </div>
                  <div className="flex gap-2">
                    <input
                      value={code}
                      onChange={(e) => { setCode(e.target.value); setErr(null); }}
                      onKeyDown={(e) => { if (e.key === "Enter") onApply(); }}
                      placeholder={t.enterCode}
                      className="flex-1 rounded-full border border-foreground/15 bg-background px-3 py-2 text-xs uppercase tracking-wider outline-none placeholder:normal-case placeholder:tracking-normal focus:border-foreground/40"
                    />
                    <button
                      onClick={onApply}
                      disabled={busy || !code.trim()}
                      className="inline-flex min-w-[4.5rem] items-center justify-center rounded-xl bg-foreground px-4 py-2 text-xs font-bold text-background transition disabled:opacity-50"
                    >
                      {busy ? (
                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                      ) : (
                        t.apply
                      )}
                    </button>
                  </div>
                  {err && <p className="mt-1.5 text-[11px] text-destructive">{err}</p>}
                </>
              )}
            </div>

            <dl className="space-y-1.5 text-xs">
              <div className="flex justify-between">
                <dt className="text-muted-foreground">{t.subtotal}</dt>
                <dd className="font-semibold">{formatMoney(subtotal, country)}</dd>
              </div>
              {bundleDiscount > 0 && (
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">{t.bundleDiscount}</dt>
                  <dd className="font-semibold text-foreground">−{formatMoney(bundleDiscount, country)}</dd>
                </div>
              )}
              {couponDiscount > 0 && (
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">{t.coupon}</dt>
                  <dd className="font-semibold">−{formatMoney(couponDiscount, country)}</dd>
                </div>
              )}
              <div className="flex items-center justify-between border-t border-foreground/10 pt-2 text-sm">
                <dt className="font-bold">{t.total}</dt>
                <dd className="font-display text-xl">{formatMoney(total, country)}</dd>
              </div>
            </dl>

            <button
              type="button"
              onClick={onCheckout}
              disabled={creating}
              className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-foreground px-6 py-3 text-sm font-bold text-background transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Lock weight="fill" className="h-4 w-4" />
              {creating ? t.starting : t.secureCheckout}
            </button>
            <p className="mt-2 text-center text-[11px] text-muted-foreground">
              {t.joinPros}
            </p>
          </footer>
        )}
      </aside>
    </div>
  );
}